import React, { useEffect, useState } from 'react';
import {
    Container, Tabs, Tab, Form, Button, Alert,
    Card, Row, Col, Spinner, Modal, Badge
} from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import firebase from '../tools/firebase';
import logo from '../assets/LaunchScreen@2x.png';
import PlacesAutocomplete from './PlacesAutocomplete';
import Footer from '../components/Footer';
import style from '../style/admin.module.css';

// ─── Truck Info ───────────────────────────────────────────────────────────────

const TruckInfoSection = ({ truckId, initialData }) => {
    const social = initialData.socialmedia || [];
    const findLink = key => (social.find(s => s.key === key) || {}).link || '';

    const [form, setForm] = useState({
        title:       initialData.title       || '',
        description: initialData.description || '',
        image:       initialData.image        || '',
        facebook:    findLink('facebook'),
        instagram:   findLink('instagram'),
        twitter:     findLink('twitter'),
        youtube:     findLink('youtube'),
    });
    const [saving, setSaving]   = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError]     = useState('');

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSuccess(false);
        setError('');
        try {
            const socialmedia = ['facebook', 'instagram', 'twitter', 'youtube']
                .filter(k => form[k])
                .map(k => ({ key: k, link: form[k] }));
            await firebase.firestore().collection('trucks').doc(truckId).update({
                title:       form.title,
                description: form.description,
                image:       form.image,
                socialmedia,
            });
            setSuccess(true);
        } catch {
            setError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form onSubmit={handleSave} className={style.sectionForm}>
            {success && <Alert variant="success" onClose={() => setSuccess(false)} dismissible>Saved!</Alert>}
            {error   && <Alert variant="danger">{error}</Alert>}

            <Form.Group>
                <Form.Label>Truck Name</Form.Label>
                <Form.Control
                    value={form.title}
                    onChange={e => set('title', e.target.value)}
                    required
                />
            </Form.Group>

            <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.description}
                    onChange={e => set('description', e.target.value)}
                />
            </Form.Group>

            <Form.Group>
                <Form.Label>Hero Image URL</Form.Label>
                <Form.Control
                    value={form.image}
                    onChange={e => set('image', e.target.value)}
                    placeholder="https://..."
                />
            </Form.Group>

            <h6 className={style.subheading}>Social Media</h6>
            {['facebook', 'instagram', 'twitter', 'youtube'].map(platform => (
                <Form.Group key={platform}>
                    <Form.Label className={style.capitalize}>{platform}</Form.Label>
                    <Form.Control
                        value={form[platform]}
                        onChange={e => set(platform, e.target.value)}
                        placeholder={`https://${platform}.com/yourpage`}
                    />
                </Form.Group>
            ))}

            <Button type="submit" className={style.primaryBtn} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
            </Button>
        </Form>
    );
};

// ─── Menu ─────────────────────────────────────────────────────────────────────

const MENU_CATEGORIES = ['Entrees', 'Desserts', 'Drinks', 'Extras'];

const MenuItemModal = ({ show, onHide, onSave, item }) => {
    const [form, setForm] = useState({ name: '', price: '', image: '', active: true });

    useEffect(() => {
        setForm({
            name:   item?.name  || '',
            price:  item?.price || '',
            image:  item?.image || '',
            active: item?.active !== false, // default true for new items
        });
    }, [item, show]);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{item?.id ? 'Edit Item' : 'Add Item'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        value={form.name}
                        onChange={e => set('name', e.target.value)}
                        required
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Price</Form.Label>
                    <Form.Control
                        value={form.price}
                        onChange={e => set('price', e.target.value)}
                        placeholder="e.g. 8.99"
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Image URL</Form.Label>
                    <Form.Control
                        value={form.image}
                        onChange={e => set('image', e.target.value)}
                        placeholder="https://..."
                    />
                </Form.Group>
                <Form.Group className={style.activeToggleGroup}>
                    <div className={style.activeToggleRow}>
                        <div>
                            <div className={style.activeToggleLabel}>Visibility</div>
                            <div className={style.activeToggleHint}>
                                {form.active ? 'Visible to customers' : 'Hidden from customers'}
                            </div>
                        </div>
                        <Form.Check
                            type="switch"
                            id="menu-item-active"
                            checked={form.active}
                            onChange={e => set('active', e.target.checked)}
                            className={style.activeSwitch}
                        />
                    </div>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button className={style.primaryBtn} onClick={() => onSave(form)}>
                    {item?.id ? 'Save Changes' : 'Add Item'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const MenuSection = ({ truckId }) => {
    const [activeCategory, setActiveCategory] = useState('Entrees');
    const [items, setItems]       = useState({});
    const [loading, setLoading]   = useState(false);
    const [modalOpen, setModal]   = useState(false);
    const [editingItem, setEditing] = useState(null);
    const [refreshKey, setRefresh]  = useState(0);

    useEffect(() => {
        setLoading(true);
        const db = firebase.firestore();
        Promise.all(
            MENU_CATEGORIES.map(cat =>
                db.collection('trucks').doc(truckId).collection(cat).get()
                    .then(snap => {
                        const list = [];
                        snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                        return { cat, list };
                    })
            )
        )
            .then(results => {
                const map = {};
                results.forEach(({ cat, list }) => { map[cat] = list; });
                setItems(map);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [truckId, refreshKey]);

    const handleSave = async (form) => {
        const db = firebase.firestore();
        const colRef = db.collection('trucks').doc(truckId).collection(activeCategory);
        const data = {
            name:   form.name   || '',
            price:  form.price  || '',
            image:  form.image  || '',
            active: form.active !== false,
        };
        if (editingItem?.id) {
            await colRef.doc(editingItem.id).update(data);
        } else {
            await colRef.add(data);
        }
        setModal(false);
        setRefresh(k => k + 1);
    };

    const handleToggleActive = async (item) => {
        await firebase.firestore()
            .collection('trucks').doc(truckId)
            .collection(activeCategory).doc(item.id)
            .update({ active: !item.active });
        setRefresh(k => k + 1);
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete "${item.name}"?`)) return;
        await firebase.firestore()
            .collection('trucks').doc(truckId)
            .collection(activeCategory).doc(item.id)
            .delete();
        setRefresh(k => k + 1);
    };

    return (
        <div>
            <Tabs
                activeKey={activeCategory}
                onSelect={cat => setActiveCategory(cat)}
                className={style.innerTabs}
            >
                {MENU_CATEGORIES.map(cat => (
                    <Tab key={cat} eventKey={cat} title={cat}>
                        <div className={style.menuTabContent}>
                            <Button
                                className={style.primaryBtn}
                                size="sm"
                                onClick={() => { setEditing(null); setModal(true); }}
                            >
                                + Add Item
                            </Button>

                            {loading ? (
                                <div className={style.centered}>
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <Row className="mt-3">
                                    {(items[cat] || []).length === 0 ? (
                                        <Col>
                                            <p className={style.emptyMsg}>No items yet.</p>
                                        </Col>
                                    ) : (
                                        (items[cat] || []).map(item => (
                                            <Col key={item.id} xs={12} sm={6} md={4} className="mb-3">
                                                <Card className={`${style.menuCard} ${item.active === false ? style.menuCardInactive : ''}`}>
                                                    {item.image && (
                                                        <div className={style.menuCardImgWrap}>
                                                            <Card.Img
                                                                variant="top"
                                                                src={item.image}
                                                                className={style.menuCardImg}
                                                            />
                                                            {item.active === false && (
                                                                <div className={style.inactiveOverlay}>Inactive</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    <Card.Body className={style.menuCardBody}>
                                                        <div className={style.menuCardHeader}>
                                                            <Card.Title className={style.menuCardTitle}>
                                                                {item.name}
                                                            </Card.Title>
                                                            <span className={item.active === false ? style.statusBadgeInactive : style.statusBadgeActive}>
                                                                {item.active === false ? 'Inactive' : 'Active'}
                                                            </span>
                                                        </div>
                                                        {item.price && (
                                                            <span className={style.menuCardPrice}>
                                                                ${item.price}
                                                            </span>
                                                        )}
                                                        <div className={style.cardActions}>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                onClick={() => { setEditing(item); setModal(true); }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant={item.active === false ? 'outline-success' : 'outline-warning'}
                                                                onClick={() => handleToggleActive(item)}
                                                            >
                                                                {item.active === false ? 'Activate' : 'Deactivate'}
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDelete(item)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))
                                    )}
                                </Row>
                            )}
                        </div>
                    </Tab>
                ))}
            </Tabs>

            <MenuItemModal
                show={modalOpen}
                onHide={() => setModal(false)}
                onSave={handleSave}
                item={editingItem}
            />
        </div>
    );
};

// ─── Schedule ─────────────────────────────────────────────────────────────────

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ScheduleModal = ({ show, onHide, onSave, entry }) => {
    const [type, setType] = useState('specific');
    const [form, setForm] = useState({
        type:      'specific',
        dayOfWeek: '',
        date:      '',
        location:  '',
        startTime: '',
        endTime:   '',
        notes:     '',
    });

    useEffect(() => {
        if (entry) {
            setType(entry.type || 'specific');
            setForm({
                type:      entry.type      || 'specific',
                dayOfWeek: entry.dayOfWeek || '',
                date:      entry.date      || '',
                location:  entry.location  || '',
                startTime: entry.startTime || '',
                endTime:   entry.endTime   || '',
                notes:     entry.notes     || '',
            });
        } else {
            setType('specific');
            setForm({ type: 'specific', dayOfWeek: '', date: '', location: '', startTime: '', endTime: '', notes: '' });
        }
    }, [entry, show]);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleTypeChange = (val) => {
        setType(val);
        set('type', val);
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{entry?.id ? 'Edit Schedule Entry' : 'Add Schedule Entry'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>Entry Type</Form.Label>
                    <div>
                        <Form.Check
                            inline
                            type="radio"
                            label="Specific Date"
                            checked={type === 'specific'}
                            onChange={() => handleTypeChange('specific')}
                        />
                        <Form.Check
                            inline
                            type="radio"
                            label="Recurring Weekly"
                            checked={type === 'recurring'}
                            onChange={() => handleTypeChange('recurring')}
                        />
                    </div>
                </Form.Group>

                {type === 'specific' ? (
                    <Form.Group>
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={form.date}
                            onChange={e => set('date', e.target.value)}
                        />
                    </Form.Group>
                ) : (
                    <Form.Group>
                        <Form.Label>Day of Week</Form.Label>
                        <Form.Control
                            as="select"
                            value={form.dayOfWeek}
                            onChange={e => set('dayOfWeek', e.target.value)}
                        >
                            <option value="">Select day…</option>
                            {DAYS.map(d => <option key={d}>{d}</option>)}
                        </Form.Control>
                    </Form.Group>
                )}

                <Form.Group>
                    <Form.Label>Location</Form.Label>
                    <PlacesAutocomplete
                        value={form.location}
                        onChange={val => set('location', val)}
                        placeholder="Address or location name"
                        className={`form-control ${style.placesInput}`}
                    />
                </Form.Group>

                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label>Start Time</Form.Label>
                            <Form.Control
                                type="time"
                                value={form.startTime}
                                onChange={e => set('startTime', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group>
                            <Form.Label>End Time</Form.Label>
                            <Form.Control
                                type="time"
                                value={form.endTime}
                                onChange={e => set('endTime', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={form.notes}
                        onChange={e => set('notes', e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button className={style.primaryBtn} onClick={() => onSave(form)}>
                    {entry?.id ? 'Save Changes' : 'Add Entry'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const ScheduleSection = ({ truckId }) => {
    const [entries, setEntries]     = useState([]);
    const [loading, setLoading]     = useState(true);
    const [modalOpen, setModal]     = useState(false);
    const [editingEntry, setEditing] = useState(null);
    const [refreshKey, setRefresh]   = useState(0);

    useEffect(() => {
        setLoading(true);
        firebase.firestore()
            .collection('trucks').doc(truckId)
            .collection('Schedule')
            .get()
            .then(snap => {
                const list = [];
                snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                list.sort((a, b) => {
                    // Recurring entries go after specific dates
                    if (a.type === 'recurring' && b.type !== 'recurring') return 1;
                    if (b.type === 'recurring' && a.type !== 'recurring') return -1;
                    return (a.date || a.dayOfWeek || '').localeCompare(b.date || b.dayOfWeek || '');
                });
                setEntries(list);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [truckId, refreshKey]);

    const handleSave = async (form) => {
        const db = firebase.firestore();
        const colRef = db.collection('trucks').doc(truckId).collection('Schedule');
        const data = {
            type:      form.type      || 'specific',
            dayOfWeek: form.dayOfWeek || '',
            date:      form.date      || '',
            location:  form.location  || '',
            startTime: form.startTime || '',
            endTime:   form.endTime   || '',
            notes:     form.notes     || '',
        };
        if (editingEntry?.id) {
            await colRef.doc(editingEntry.id).update(data);
        } else {
            await colRef.add(data);
        }
        setModal(false);
        setRefresh(k => k + 1);
    };

    const handleDelete = async (entry) => {
        if (!window.confirm('Delete this schedule entry?')) return;
        await firebase.firestore()
            .collection('trucks').doc(truckId)
            .collection('Schedule').doc(entry.id)
            .delete();
        setRefresh(k => k + 1);
    };

    const formatTime = (entry) => {
        if (entry.startTime && entry.endTime) return `${entry.startTime} – ${entry.endTime}`;
        return entry.startTime || entry.endTime || '';
    };

    return (
        <div className={style.sectionForm}>
            <Button
                className={style.primaryBtn}
                size="sm"
                onClick={() => { setEditing(null); setModal(true); }}
            >
                + Add Schedule Entry
            </Button>

            {loading ? (
                <div className={style.centered}>
                    <Spinner animation="border" size="sm" />
                </div>
            ) : entries.length === 0 ? (
                <p className={`${style.emptyMsg} mt-3`}>No schedule entries yet.</p>
            ) : (
                <div className={style.scheduleList}>
                    {entries.map(entry => (
                        <div key={entry.id} className={style.scheduleCard}>
                            <div className={style.scheduleCardMain}>
                                <div className={style.scheduleLabel}>
                                    {entry.type === 'recurring' ? (
                                        <Badge className={style.badgeRecurring}>Weekly</Badge>
                                    ) : (
                                        <Badge className={style.badgeSpecific}>Date</Badge>
                                    )}
                                </div>
                                <div className={style.scheduleWhen}>
                                    {entry.type === 'recurring' ? entry.dayOfWeek : entry.date}
                                </div>
                                {entry.location && (
                                    <div className={style.scheduleLocation}>{entry.location}</div>
                                )}
                                {formatTime(entry) && (
                                    <div className={style.scheduleTime}>{formatTime(entry)}</div>
                                )}
                                {entry.notes && (
                                    <div className={style.scheduleNotes}>{entry.notes}</div>
                                )}
                            </div>
                            <div className={style.cardActions}>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => { setEditing(entry); setModal(true); }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(entry)}
                                >
                                    Delete
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ScheduleModal
                show={modalOpen}
                onHide={() => setModal(false)}
                onSave={handleSave}
                entry={editingEntry}
            />
        </div>
    );
};

// ─── Main TruckAdmin Component ────────────────────────────────────────────────

const TruckAdmin = ({ match, history }) => {
    const truckId = match.params.truckId;
    const [truckData, setTruckData] = useState(null);
    const [loading, setLoading]     = useState(true);

    useEffect(() => {
        firebase.firestore().collection('trucks').doc(truckId).get()
            .then(doc => { if (doc.exists) setTruckData(doc.data()); })
            .finally(() => setLoading(false));
    }, [truckId]);

    const handleSignOut = () => {
        firebase.auth().signOut().then(() => history.push('/admin'));
    };

    if (loading) {
        return (
            <div className={style.centered} style={{ marginTop: '3rem' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (!truckData) {
        return (
            <Container className="mt-4">
                <p>Truck not found.</p>
                <Button variant="link" onClick={() => history.push('/admin/dashboard')}>
                    ← Back to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <div className={style.adminPage}>
            <div
                className={style.truckAdminHero}
                style={truckData.image ? { backgroundImage: `url(${truckData.image})` } : {}}
            >
                <div className={style.truckAdminHeroOverlay} />
                <Container>
                    <div className={style.truckAdminHeroInner}>
                        <div className={style.truckAdminHeroLeft}>
                            <button
                                className={style.backLink}
                                onClick={() => history.push('/admin/dashboard')}
                            >
                                ← My Trucks
                            </button>
                            <div className={style.truckAdminHeroTitle}>
                                <img src={logo} alt="Grub Trucks" className={style.truckAdminLogo} />
                                <div>
                                    <h2 className={style.topBarTitle}>{truckData.title}</h2>
                                    <a
                                        href={`/trucks/${truckId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={style.viewProfileLink}
                                    >
                                        View Public Profile ↗
                                    </a>
                                </div>
                            </div>
                        </div>
                        <Button className={style.signOutBtn} onClick={handleSignOut}>
                            Sign Out
                        </Button>
                    </div>
                </Container>
            </div>

            <Container className={style.pageContent}>
                <Tabs defaultActiveKey="info" className={style.mainTabs}>
                    <Tab eventKey="info" title="Truck Info">
                        <div className={style.tabPanel}>
                            <TruckInfoSection truckId={truckId} initialData={truckData} />
                        </div>
                    </Tab>
                    <Tab eventKey="menu" title="Menu">
                        <div className={style.tabPanel}>
                            <MenuSection truckId={truckId} />
                        </div>
                    </Tab>
                    <Tab eventKey="schedule" title="Schedule">
                        <div className={style.tabPanel}>
                            <ScheduleSection truckId={truckId} />
                        </div>
                    </Tab>
                </Tabs>
            </Container>
            <Footer />
        </div>
    );
};

export default withRouter(TruckAdmin);
